import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import { useLang } from '../../contexts/langContext';
import TagPicker from './TagPicker';
import { updateProject } from '../../services/projects';
import { updatePptxFile } from '../../services/pptxFiles';
import { updateMaterial } from '../../services/materials';
import { updateImage } from '../../services/images';

export type EntityType = 'project' | 'pptx' | 'material' | 'image';

interface ManageTagsDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityType;
  entity: any;
  onSaved: (newTagIds: string[]) => void;
  projectId?: string; // For materials that need project context
}

const ManageTagsDialog: React.FC<ManageTagsDialogProps> = ({
  open,
  onClose,
  entityType,
  entity,
  onSaved,
  projectId,
}) => {
  const { lang } = useLang();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && entity) {
      setSelectedTagIds(entity.tagIds || []);
      setError(null);
    }
  }, [open, entity]);

  const handleSave = async () => {
    if (!entity) {
      console.error('Entity is not defined');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      switch (entityType) {
        case 'project':
          await updateProject(entity._id, { tagIds: selectedTagIds });
          break;
        case 'pptx':
          await updatePptxFile(entity._id, { tagIds: selectedTagIds });
          break;
        case 'material':
          await updateMaterial(entity._id, { tagIds: selectedTagIds, projectId });
          break;
        case 'image':
          await updateImage(entity._id, entity.type, entity.alt, selectedTagIds, entity.url);
          break;
        default:
          throw new Error('Unknown entity type');
      }

      onSaved(selectedTagIds);
      onClose();
    } catch (err: any) {
      console.error('Error updating tags:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update tags');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const getEntityName = () => {
    switch (entityType) {
      case 'project':
        return entity?.title || 'Project';
      case 'pptx':
        return entity?.filename || 'PPTX File';
      case 'material':
        return entity?.fileMetadata?.filename || 'Material';
      case 'image':
        return entity?.alt || 'Image';
      default:
        return 'Item';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {lang === 'en' ? 'Manage Tags' : '管理标签'}
        {entity && (
          <Box component="span" sx={{ display: 'block', fontSize: '0.8em', color: 'text.secondary', mt: 0.5 }}>
            {getEntityName()}
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TagPicker
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
            label={lang === 'en' ? 'Tags' : '标签'}
            placeholder={lang === 'en' ? 'Select tags' : '选择标签'}
          />
        </Box>
        {error && (
          <Box sx={{ mt: 2, color: 'error.main', fontSize: '0.875rem' }}>
            {error}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {lang === 'en' ? 'Cancel' : '取消'}
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {lang === 'en' ? 'Save' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageTagsDialog; 